<template>
  <div class="card border-0 project-info-card">
    <div class="card-body p-0">
      <div class="project-info-grid">
        <!-- ================================================= -->
        <!-- ROW 1 -->
        <!-- ================================================= -->

        <!-- PROYEK -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-file-alt"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Proyek
            </div>

            <div class="info-value">
              {{ detail.kode_proyek || '-' }}
            </div>

            <div
              v-if="detail.nama_proyek"
              class="info-subvalue"
              :title="detail.nama_proyek"
            >
              {{ detail.nama_proyek }}
            </div>
          </div>
        </div>

        <!-- TANGGAL KONTRAK -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-calendar-alt"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Tanggal Kontrak
            </div>

            <div class="info-value">
              {{ dateFormat(detail.tanggal_mulai) }}
              -
              {{ dateFormat(detail.tanggal_berakhir) }}
            </div>
          </div>
        </div>

        <!-- MEKANISME -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-wallet"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Mekanisme
            </div>

            <div class="info-value">
              {{ detail.mekanisme_pembayaran || '-' }}
            </div>
          </div>
        </div>

        <!-- JUMLAH TAHAPAN -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-list-ul"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Jumlah Tahapan
            </div>

            <div class="info-value">
              {{ detail.jumlah_tahapan_pembayaran || '0' }}
            </div>
          </div>
        </div>

        <!-- NILAI KONTRAK -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-money-bill"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Nilai Kontrak
            </div>

            <div class="info-value">
              {{ formatRupiah(detail.nilai_kontrak_belum_ppn) }}
            </div>
          </div>
        </div>

        <!-- ================================================= -->
        <!-- ROW 2 -->
        <!-- ================================================= -->

        <!-- NILAI PRORATE -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-calculator"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Nilai Prorate
            </div>

            <div class="info-value">
              {{ formatRupiah(detail.nilai_kontrak_prorate) }}
            </div>
          </div>
        </div>

        <!-- NILAI BEBAN -->
        <div class="info-item">
          <div class="info-icon icon-warning">
            <i class="uil uil-money-withdraw"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Nilai Beban
            </div>

            <div class="info-value">
              {{ formatRupiah(safeBeban) }}
            </div>
          </div>
        </div>

        <!-- TOTAL PANJAR -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-wallet"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Total Panjar
            </div>

            <div class="info-value">
              {{ formatRupiah(safePanjar) }}
            </div>
          </div>
        </div>

        <!-- SELISIH -->
        <div class="info-item">
          <div
            class="info-icon"
            :class="selisih < 0 ? 'icon-danger' : 'icon-success'"
          >
            <i class="uil uil-exchange"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Selisih
            </div>

            <div
              class="info-value"
              :class="selisih < 0 ? 'text-danger' : 'text-success'"
            >
              {{ formatRupiah(selisih) }}
            </div>

            <div class="info-subvalue">
              Beban - Panjar
            </div>
          </div>
        </div>

        <!-- MARGIN -->
        <div class="info-item">
          <div
            class="info-icon"
            :class="marginPersen < 0 ? 'icon-danger' : 'icon-success'"
          >
            <i class="uil uil-chart-growth"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Beban
            </div>

            <div
              class="info-value"
              :class="marginPersen < 0 ? 'text-danger' : 'text-success'"
            >
              {{ marginPersen.toFixed(2) }}%
            </div>

            <div class="info-subvalue">
              Panjar / Beban
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ProjectInfo',

  props: {
    detail: {
      type: Object,
      required: true,
    },

    totalBeban: {
      type: [Number, String],
      default: 0,
    },

    totalPanjar: {
      type: [Number, String],
      default: 0,
    },
  },

  computed: {
    /**
     * Memastikan nilai beban selalu Number.
     */
    safeBeban() {
      return Number(this.totalBeban) || 0
    },

    /**
     * Memastikan nilai panjar selalu Number.
     */
    safePanjar() {
      return Number(this.totalPanjar) || 0
    },

    /**
     * Selisih antara beban dan panjar.
     */
    selisih() {
      return this.safeBeban - this.safePanjar
    },

    /**
     * Margin dalam persentase.
     *
     * Panjar / Beban * 100
     */
    marginPersen() {
      if (this.safeBeban === 0) {
        return 0
      }

      return (this.safePanjar / this.safeBeban) * 100
    },
  },

  methods: {
    formatRupiah(value) {
      return `Rp ${Number(value || 0).toLocaleString('id-ID')}`
    },

    dateFormat(date) {
      return date ? moment(date).format('DD/MM/YYYY') : '-'
    },
  },
}
</script>

<style scoped>
/* =========================================
   PROJECT INFO CARD
   ========================================= */

.project-info-card {
  border: 1px solid #cfe2ff !important;
  border-radius: 8px;
  background: #f8fbff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

/* =========================================
   GRID
   ========================================= */

.project-info-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

/* =========================================
   INFO ITEM
   ========================================= */

.info-item {
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 92px;
  padding: 12px 15px;

  border-right: 1px solid #dbe7f5;
  border-bottom: 1px solid #dbe7f5;
}

/* 5 kolom desktop */
.info-item:nth-child(5n) {
  border-right: 0;
}

/* 5 item terakhir = row terakhir */
.info-item:nth-last-child(-n + 5) {
  border-bottom: 0;
}

/* =========================================
   ICON
   ========================================= */

.info-icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-right: 12px;
  border-radius: 50%;

  font-size: 20px;
}

.icon-primary {
  color: #3867f0;
  background: #e8f1ff;
}

.icon-success {
  color: #20bf82;
  background: #e2f7ed;
}

.icon-warning {
  color: #3867f0;
  background: #e8f1ff;
}

.icon-danger {
  color: #f46a6a;
  background: #fdeaea;
}

/* =========================================
   CONTENT
   ========================================= */

.info-content {
  min-width: 0;
  flex: 1;
}

.info-label {
  margin-bottom: 3px;

  color: #53627a;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
}

.info-value {
  color: #15213b;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;

  word-break: break-word;
}

.info-subvalue {
  margin-top: 2px;

  color: #7c8799;
  font-size: 11px;
  line-height: 1.3;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* =========================================
   TABLET
   3 KOLOM
   ========================================= */

@media (max-width: 1199.98px) {
  .project-info-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .info-item {
    min-height: 92px;
    padding: 12px 14px;

    border-right: 1px solid #dbe7f5;
    border-bottom: 1px solid #dbe7f5;
  }

  /* Reset aturan 5 kolom */
  .info-item:nth-child(5n) {
    border-right: 1px solid #dbe7f5;
  }

  /* Setiap item ke-3 = ujung kanan */
  .info-item:nth-child(3n) {
    border-right: 0;
  }

  /* Reset row terakhir desktop */
  .info-item:nth-last-child(-n + 5) {
    border-bottom: 1px solid #dbe7f5;
  }

  /* 3 item terakhir = row terakhir */
  .info-item:nth-last-child(-n + 3) {
    border-bottom: 0;
  }
}

/* =========================================
   MOBILE
   2 KOLOM
   ========================================= */

@media (max-width: 767.98px) {
  .project-info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .info-item {
    min-height: 82px;
    padding: 11px 12px;

    border-right: 1px solid #dbe7f5;
    border-bottom: 1px solid #dbe7f5;
  }

  /* Reset aturan tablet */
  .info-item:nth-child(3n) {
    border-right: 1px solid #dbe7f5;
  }

  /* Setiap item ke-2 = ujung kanan */
  .info-item:nth-child(2n) {
    border-right: 0;
  }

  /* Reset row terakhir tablet */
  .info-item:nth-last-child(-n + 3) {
    border-bottom: 1px solid #dbe7f5;
  }

  /* 2 item terakhir = row terakhir */
  .info-item:nth-last-child(-n + 2) {
    border-bottom: 0;
  }

  /* ICON */

  .info-icon {
    flex: 0 0 34px;
    width: 34px;
    height: 34px;

    margin-right: 9px;

    font-size: 17px;
  }

  /* TEXT */

  .info-label {
    font-size: 11px;
    margin-bottom: 2px;
  }

  .info-value {
    font-size: 14px;
  }

  .info-subvalue {
    font-size: 10px;
  }
}

/* =========================================
   EXTRA SMALL
   1 KOLOM
   ========================================= */

@media (max-width: 575.98px) {
  .project-info-grid {
    grid-template-columns: 1fr;
  }

  .info-item {
    min-height: 72px;
    padding: 10px 12px;

    border-right: 0 !important;
    border-bottom: 1px solid #dbe7f5 !important;
  }

  .info-item:last-child {
    border-bottom: 0 !important;
  }

  /* ICON */

  .info-icon {
    flex: 0 0 34px;
    width: 34px;
    height: 34px;

    margin-right: 10px;

    font-size: 17px;
  }

  /* TEXT */

  .info-label {
    font-size: 11px;
  }

  .info-value {
    font-size: 14px;
  }

  .info-subvalue {
    font-size: 10px;
  }
}
</style>
